// Streams (ADR-020) — configure + observe the network's stream pipelines.
//
// The section used to be called "Routes", which named the CONFIGURATION and not the
// thing being configured: an operator opens this page because a call is silent, and
// a route is the shape that call runs through. It is "Streams" now (the old /routes
// URL redirects, so a bookmark still lands here), and the live streams — which were
// a tab inside Overview's Activity block, five clicks from the routes that produce
// them — are here, inside the routes that produce them (WB6).
//
// Three panels, one after the other (UX20 — no tab bar: the answers about a network's
// streams are read together, and a tab bar makes most of the page invisible to
// somebody who does not yet know which part holds their answer). Each is collapsible
// and remembers its own state.
//
// WB6 — the first two panels used to be two: "Live streams" (stream sessions) and
// "Running streams" (route instances), with a "Map" button in the first that opened a
// drawer drawing the second. They are ONE table now, because they were never two
// subjects: a live stream exists BECAUSE a route hop opened it, and an operator who
// came to find out why a call went quiet was made to read a stream id out of one table
// and hunt for it in the other. The table lists ROUTES; a row expands to the streams
// that route is actually carrying, drawn beside the route's own graph. Streams that
// belong to no route (a plain streaming tool call) keep their own list below, so
// merging the tables does not quietly stop listing them.
//
// The order is observation first, configuration last — the page is opened mid-call
// far more often than mid-edit:
//   • Editor — list routes of the selected network, build/edit a hop GRAPH (CL13:
//     each hop picks a streaming tool and names the producer it reads — the route
//     source or an earlier hop — and one hop is the route's output; the rest of the
//     terminal hops are taps), declare sinks, save (= a new version), and inspect
//     version history. The editor blocks a shape the server would refuse — a `from`
//     pointing forwards, an `output_hop` naming no hop — and an incompatible channel
//     pair along a REAL edge; the server (R1) is the source of truth and still rejects
//     a bypass (a 400 surfaces as a friendly message). A chain the catalog cannot
//     describe is reported rather than refused: `GET /tools` carries no channel
//     declarations, and refusing on that basis blocks routes the gateway accepts.
//   • Routes (WB6) — route instances (REST seed + route.* bus events): which
//     provider served each hop, the pinned version, the REAL per-hop throughput /
//     latency / drops / delivery path (SR14), and status — filtered (default:
//     running now), searchable across route/description/tool/stream id, and paged.
//     A row EXPANDS in place to the streams that route is carrying, listed from the
//     instance's own source/output/per-hop stream ids and joined to `GET /streams` by
//     identity, beside the route drawn with the same <RouteGraph/> the editor uses.
//     Below the table: the streams no route claims — a plain streaming tool call —
//     which used to be a table of its own and must not vanish with it.
//
//     The throughput column used to render `hops.length` and call it throughput — the
//     header comment said "throughput proxy (hop count)" in as many words. A hop count
//     is a property of the ROUTE; it does not change when a chain stalls, which is the
//     one moment somebody is looking at it. `formatThroughput` is exported and guarded
//     by a test so the proxy cannot quietly come back.
//   • Latency (SR19) — the same window read as a BUDGET: per-stage p50/p95 against
//     the epic's target table, and one verdict on the 800 ms mouth-to-ear goal. It
//     reads two clocks (the assistant's voice stage marks and the gateway's per-hop
//     transport share) and labels every row with the one it came from, because they
//     are not the same clock and a budget that mixes them silently is fiction.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useResource } from '../lib/useResource';
import { useStore } from '../store/store';
import {
  Card,
  Loading,
  ErrorState,
  Badge,
  StatCard,
  Segmented,
  StatusBadge,
  bytesHuman,
} from '../components/ui';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { usePersistentState } from '../lib/usePersistentState';
import {
  MOUTH_TO_EAR_TARGET_MS,
  PTT_ASR,
  PTT_TARGET_MS,
  REALTIME_ASR,
  budgetFill,
  budgetSeverity,
  budgetVerdict,
  buildBudget,
  byMode,
  formatStat,
  replyTextRow,
  worstHopLatency,
  type BudgetRow,
} from '../lib/latencyBudget';
import { Pager, pageSlice } from '../components/Pager';
import { Drawer } from '../components/Drawer';
import { idLabel, routeLabel, streamLabel, toolLabel } from '../lib/friendlyNames';
import {
  instanceLegs,
  matchesQuery,
  routeHaystack,
  splitByRoute,
  type StreamLeg,
} from '../lib/routeStreams';
import { StreamViewer } from '../components/StreamViewer';
import { wsUrl } from '../config';
import type { CatalogTool, Network, Route, RouteHop, RouteInstance, RouteSkippedHop } from '../api/schemas';
import { RouteGraph, type HopLiveState } from '../components/RouteGraph';
import {
  FROM_SOURCE,
  buildRouteGraph,
  firstIncompatibleEdge,
  markAsTap,
  outputIndex,
  removeHop,
  resolveFrom,
  shapeFromInstance,
  validateRouteShape,
} from '../lib/routeGraph';
import type { EdgeMismatch } from '../lib/routeGraph';

/** The panels, in the order they are drawn (exported so the order is assertable). */
export const STREAM_PANELS = ['routes', 'latency', 'editor'] as const;
export type StreamPanel = (typeof STREAM_PANELS)[number];

export function Streams() {
  const nets = useResource(() => api.networks(), []);
  const [networkId, setNetworkId] = useState<string | null>(null);

  const networks = nets.data?.items ?? [];
  const activeNetwork = networkId ?? networks[0]?.id ?? null;

  return (
    <div className="view">
      <div className="view-head">
        <h1>Streams</h1>
      </div>

      <Card title="Network">
        {nets.loading ? (
          <Loading what="Loading networks…" />
        ) : nets.error || !networks.length ? (
          <ErrorState message={nets.error ?? 'no networks'} onRetry={nets.reload} />
        ) : (
          <select
            className="input"
            value={activeNetwork ?? ''}
            onChange={(e) => setNetworkId(e.target.value)}
            aria-label="network"
          >
            {networks.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name} (/{n.slug})
              </option>
            ))}
          </select>
        )}
      </Card>

      {activeNetwork && (
        <>
          {/* UX23 — the editor sits directly under the network picker: choosing a
              network and then editing what runs in it is one gesture, and the editor
              was three panels away from the control that decides what it edits. It
              opens FOLDED — CollapsibleSection unmounts a folded body, so a reader
              who came to look at a stream does not pay for the editor's route and
              catalog fetches. */}
          <CollapsibleSection title="Stream editor" storageKey="streams.collapsed:editor" defaultCollapsed>
            <RoutesEditor network={networks.find((n) => n.id === activeNetwork)!} />
          </CollapsibleSection>

          {/* WB6 — one table where there were two. A row is a ROUTE (what was running, with the
              SR14 numbers it was running at); expanding it shows the live streams that route is
              carrying and the graph of the route itself, which is the pair of facts that explains
              a stream's existence. */}
          <CollapsibleSection title="Routes" storageKey="streams.collapsed:routes">
            <RouteStreamsPanel
              networkId={activeNetwork}
              networkSlug={networks.find((n) => n.id === activeNetwork)?.slug ?? ''}
            />
          </CollapsibleSection>

          {/* SR19 — the same instances, read as a BUDGET rather than as a list. "Route instances"
              answers "is it running"; this answers "is it fast enough, and if not, which stage
              spent the milliseconds" — which is the question a silent-seeming call actually
              raises. It sits next to the instances because the two share a window. */}
          <CollapsibleSection title="Latency" storageKey="streams.collapsed:latency">
            <LatencyBudget networkId={activeNetwork} />
          </CollapsibleSection>

        </>
      )}
    </div>
  );
}

// --- editor ---------------------------------------------------------------

function RoutesEditor({ network }: { network: Network }) {
  const routes = useResource(() => api.routes(network.id), [network.id]);
  const tools = useResource(() => api.tools(), []);
  const [editing, setEditing] = useState<Route | 'new' | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const remove = async (name: string) => {
    setDeleteError(null);
    try {
      await api.deleteRoute(network.id, name);
      routes.reload();
    } catch (e) {
      // The server refuses while instances of the route are live, and that refusal is the useful
      // half: deleting a route out from under a call in progress is the thing being prevented.
      setDeleteError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const streamingTools = useMemo(
    () =>
      (tools.data?.items ?? []).filter(
        (t) => t.streaming && t.network_slug === network.slug,
      ),
    [tools.data, network.slug],
  );

  return (
    <>
      {/* UX23 — no Card in here: this component is already inside the "Stream editor"
          section, and a titled card inside a titled section is the same heading drawn
          twice with two borders around it. */}
      <div className="panel-actions">
        <span className="muted small">Streams declared in this network</span>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          + New stream
        </button>
      </div>
      <div className="panel-body">
        {routes.loading ? (
          <Loading what="Loading streams…" />
        ) : routes.error || !routes.data ? (
          <ErrorState message={routes.error ?? 'no data'} onRetry={routes.reload} />
        ) : routes.data.items.length === 0 ? (
          <p className="muted">No streams declared in this network yet — create one.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Version</th>
                <th>Hops</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {routes.data.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <b>{r.name}</b>
                  </td>
                  <td>
                    <Badge tone="muted">v{r.version}</Badge>
                  </td>
                  <td className="small mono">{summarizeShape(r.hops, r.output_hop)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-sm" onClick={() => setEditing(r)}>
                      Edit
                    </button>{' '}
                    <button className="btn btn-sm" onClick={() => setHistory(r.name)}>
                      History
                    </button>{' '}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => void remove(r.name)}
                      aria-label={`delete ${r.name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {deleteError && (
        <p className="error-text" role="alert">
          {deleteError}
        </p>
      )}

      {history && (
        <RouteHistory network={network} name={history} onClose={() => setHistory(null)} />
      )}

      {editing && (
        <RouteEditorForm
          network={network}
          route={editing === 'new' ? null : editing}
          streamingTools={streamingTools}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            routes.reload();
          }}
        />
      )}
    </>
  );
}

// --- SR16: version history with a hop diff, and deleting a route ----------
//
// `api.routeVersions`, `api.route(net, name, version)` and `api.deleteRoute` have existed in the
// client since R6 and were called from NOTHING but a unit test — so R6.3 ("the history shows both
// versions with a diff") was reported done while a user could not reach it at all. Hot-swapping a
// hop is the flagship two-click scenario of ADR-020; being unable to SEE what the two clicks
// changed is most of what makes it feel like guesswork.

/** One row of a hop diff: the same position in `v{n-1}` and in `v{n}`. */
export interface HopDiffRow {
  index: number;
  before: string | null;
  after: string | null;
  changed: boolean;
}

/** Compare two versions' hop chains position by position. */
export function diffHops(before: { tool: string }[], after: { tool: string }[]): HopDiffRow[] {
  const rows: HopDiffRow[] = [];
  for (let i = 0; i < Math.max(before.length, after.length); i++) {
    const b = before[i]?.tool ?? null;
    const a = after[i]?.tool ?? null;
    rows.push({ index: i, before: b, after: a, changed: b !== a });
  }
  return rows;
}

function RouteHistory({ network, name, onClose }: { network: Network; name: string; onClose(): void }) {
  const versions = useResource(() => api.routeVersions(network.id, name), [network.id, name]);
  const items = versions.data?.items ?? [];
  // Newest first from the API; the diff is always "the previous version -> this one".
  const [latest, previous] = items;
  const rows = latest && previous ? diffHops(previous.hops, latest.hops) : [];

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <h2>{name} — version history</h2>
        {versions.loading ? (
          <Loading what="Loading versions…" />
        ) : versions.error ? (
          <ErrorState message={versions.error} onRetry={versions.reload} />
        ) : items.length === 0 ? (
          <p className="muted">No versions.</p>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Hops</th>
                  <th>Latest</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={`${r.name}-${r.version}`}>
                    <td>
                      <Badge tone="muted">v{r.version}</Badge>
                    </td>
                    <td className="small mono">{summarizeShape(r.hops, r.output_hop)}</td>
                    <td>{r.is_latest ? <Badge tone="ok">latest</Badge> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 0 && (
              <>
                <h3>
                  v{previous!.version} → v{latest!.version}
                </h3>
                <table className="table" data-testid="hop-diff">
                  <thead>
                    <tr>
                      <th>Hop</th>
                      <th>v{previous!.version}</th>
                      <th>v{latest!.version}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.index} className={row.changed ? 'row-changed' : undefined}>
                        <td>{row.index}</td>
                        <td className="small mono">{row.before ?? '—'}</td>
                        <td className="small mono">
                          {row.after ?? '—'}
                          {row.changed ? <Badge tone="warn">changed</Badge> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
        <div className="drawer-actions">
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Compute the first incompatible hop pair by DOWN(N) vs UP(N+1) media-type using the catalog
// channel declarations. The comparison itself lives in ./channelTypes, which mirrors the server
// validator case by case (SR10) — the editor blocks before save so the user is not made to
// round-trip a 422, and the REASON it shows is the server's own sentence, so the two never
// disagree about why. The server stays the source of truth.
//
// CL13 moved the walk itself into `lib/routeGraph.firstIncompatibleEdge`, which follows the hops'
// declared `from` instead of their POSITION: on a branching route hop N no longer necessarily feeds
// hop N+1, so comparing consecutive entries both invents mismatches between hops that never touch
// (the VAD's output against the TTS's input) and misses the real ones (a branch against the source
// it actually reads). On a linear route the two walks are the same walk.
export function firstIncompatiblePair(
  hops: RouteHop[],
  byCanonical: (sel: string) => CatalogTool | undefined,
): EdgeMismatch | null {
  return firstIncompatibleEdge(hops, byCanonical);
}

/** The hop chain as one line for a table cell — branch-aware, so a sidecar is not read as a link.
 *
 * `hops.map(h => h.tool).join(' → ')` is a faithful picture of a route that cannot branch and a lie
 * about one that can: the cubi call route rendered as four hops in a row, which is the one shape it
 * is not. */
export function summarizeShape(
  hops: { tool: string; from?: number | string | null }[],
  outputHop?: number | null,
): string {
  if (!hops.length) return '—';
  const graph = buildRouteGraph(hops, outputHop);
  const spine: string[] = [];
  let cursor = graph.outputIndex;
  const guard = new Set<number>();
  while (cursor >= 0 && cursor < hops.length && !guard.has(cursor)) {
    guard.add(cursor);
    spine.unshift(hops[cursor]?.tool || '—');
    const src = resolveFrom(cursor, hops[cursor]);
    cursor = typeof src === 'number' ? src : -1;
  }
  const taps = graph.nodes.filter((n) => n.kind === 'hop' && n.isTap).map((n) => n.label);
  const line = spine.join(' → ') || '—';
  return taps.length ? `${line}  ·  tap: ${taps.join(', ')}` : line;
}

function RouteEditorForm({
  network,
  route,
  streamingTools,
  onClose,
  onSaved,
}: {
  network: Network;
  route: Route | null;
  streamingTools: CatalogTool[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(route?.name ?? '');
  const [hops, setHops] = useState<RouteHop[]>(route?.hops ?? []);
  // CL13 — `null` means "the last hop", which is what the route's DOWN end always was. It is kept
  // as its own piece of state rather than derived from the order, because the moment a route
  // branches, "the last hop" stops being an answer and becomes an accident of typing order.
  const [outputHop, setOutputHop] = useState<number | null>(route?.output_hop ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Map a hop selector ("service__tool") to its catalog entry (network-relative).
  const byCanonical = (sel: string): CatalogTool | undefined => {
    const canon = sel.split('__').length === 3 ? sel : `${network.slug}__${sel}`;
    return streamingTools.find((t) => t.canonical_name === canon);
  };
  const relSelector = (t: CatalogTool) =>
    `${t.service_slug ?? t.canonical_name.split('__')[1]}__${t.tool_name ?? t.canonical_name.split('__')[2]}`;

  const incompat = firstIncompatiblePair(hops, byCanonical);
  const shape = validateRouteShape(hops, outputHop);
  const shapeErrors = shape.filter((p) => p.severity === 'error' && p.code !== 'empty');
  const shapeWarnings = shape.filter((p) => p.severity === 'warn');
  const graph = buildRouteGraph(hops, outputHop);
  const effectiveOutput = outputIndex(hops.length, outputHop);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        hops,
        sinks: [{ kind: 'loopback' as const, params: {} }],
        // Sent only when it says something: `null` IS "the last hop" to the server, and a route
        // that never branched should keep writing the body it always wrote.
        ...(outputHop !== null ? { output_hop: outputHop } : {}),
      };
      if (route) {
        await api.updateRoute(network.id, route.name, body);
      } else {
        await api.createRoute(network.id, { name, ...body });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <h2>{route ? `Edit ${route.name} (→ v${route.version + 1})` : 'New route'}</h2>
          <button className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        {!route && (
          <label className="field">
            <span>Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="dialog"
              aria-label="route name"
            />
          </label>
        )}

        <h3>Hops</h3>
        <ol className="hop-chain">
          {hops.map((h, i) => {
            const src = resolveFrom(i, h);
            const node = graph.nodes.find((n) => n.kind === 'hop' && n.index === i);
            return (
              <li key={i} className="hop-row hop-row-branch">
                <select
                  className="input"
                  value={h.tool}
                  aria-label={`hop ${i}`}
                  onChange={(e) =>
                    setHops((hs) => hs.map((x, j) => (j === i ? { ...x, tool: e.target.value } : x)))
                  }
                >
                  <option value="">— select tool —</option>
                  {streamingTools.map((t) => (
                    <option key={t.canonical_name} value={relSelector(t)}>
                      {relSelector(t)}
                    </option>
                  ))}
                </select>
                {/* CL13 — the producer this hop READS. Two hops naming the same producer IS the
                    fan-out: one microphone feeding both the STT chain and a VAD sidecar. Only
                    EARLIER hops are offered, which is the same rule the server enforces and the
                    reason a cycle cannot be composed here at all. */}
                <select
                  className="input"
                  aria-label={`hop ${i} reads from`}
                  value={src === null ? FROM_SOURCE : src === undefined ? '' : String(src)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setHops((hs) =>
                      hs.map((x, j) =>
                        j === i ? { ...x, from: v === FROM_SOURCE ? FROM_SOURCE : Number(v) } : x,
                      ),
                    );
                  }}
                >
                  <option value={FROM_SOURCE}>reads: source</option>
                  {hops.slice(0, i).map((prev, j) => (
                    <option key={j} value={String(j)}>
                      reads: #{j} {prev.tool || '—'}
                    </option>
                  ))}
                </select>
                <label className="hop-flag">
                  <input
                    type="radio"
                    name="output-hop"
                    aria-label={`output hop ${i}`}
                    checked={effectiveOutput === i}
                    onChange={() => setOutputHop(i)}
                  />
                  <span>output</span>
                </label>
                <button
                  className="btn btn-sm"
                  aria-label={`mark hop ${i} as tap`}
                  disabled={effectiveOutput !== i || hops.length < 2}
                  title="A tap is a hop nothing downstream reads: the caller keeps listening to another hop, and this one's frames are handed to the opener as a side branch."
                  onClick={() => setOutputHop(markAsTap(hops, outputHop, i))}
                >
                  tap
                </button>
                {node?.isTap && <Badge tone="info">tap</Badge>}
                {node?.isOutput && <Badge tone="ok">out</Badge>}
                <button
                  className="btn btn-sm"
                  aria-label={`remove hop ${i}`}
                  onClick={() => {
                    const next = removeHop(hops, outputHop, i);
                    setHops(next.hops);
                    setOutputHop(next.outputHop);
                  }}
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ol>
        <button
          className="btn btn-sm"
          onClick={() => setHops((hs) => [...hs, { tool: '', params: {} }])}
        >
          + Add hop
        </button>
        <button
          className="btn btn-sm"
          title="A sidecar reads the route's SOURCE and is read by nothing — a VAD on the microphone. It is added terminal on purpose: the end of the call stays where it is."
          onClick={() => {
            const out = outputIndex(hops.length, outputHop);
            setHops((hs) => [...hs, { tool: '', params: {}, from: FROM_SOURCE }]);
            // Appending must not move the DOWN end onto the new hop, which is what "the last hop"
            // would have meant. Pin it to where it already was.
            if (outputHop === null && out >= 0) setOutputHop(out);
          }}
        >
          + Add sidecar (tap on source)
        </button>

        {hops.length > 0 && (
          <>
            <h3>Shape</h3>
            <RouteGraph graph={graph} title={`${name || route?.name || 'route'} shape`} />
          </>
        )}

        {shapeErrors.map((p) => (
          <p className="error-text" role="alert" key={`${p.code}-${p.hop ?? 'x'}`}>
            {p.message}
          </p>
        ))}
        {shapeWarnings.map((p) => (
          <p className="warn-text" key={`${p.code}-${p.hop ?? 'x'}`}>
            {p.message}
          </p>
        ))}
        {incompat && (
          // A chain the catalog cannot describe is REPORTED, not refused: `GET /tools` carries no
          // channel declarations on a real stand, and refusing on that basis disabled Save for every
          // multi-hop route on cubi — including the call route the gateway had just accepted.
          <p className={incompat.severity === 'error' ? 'error-text' : 'warn-text'}
             role={incompat.severity === 'error' ? 'alert' : undefined}>
            {incompat.message}
          </p>
        )}
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}

        <div className="drawer-actions">
          <button
            className="btn btn-primary"
            disabled={
              busy ||
              incompat?.severity === 'error' ||
              shapeErrors.length > 0 ||
              hops.length === 0 ||
              (!route && !name) ||
              hops.some((h) => !h.tool)
            }
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : route ? 'Save new version' : 'Create route'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- WB6: routes and the streams they carry, in ONE table -------------------
//
// Two tables became one. "Running streams" listed route instances; "Live streams" listed stream
// sessions and offered a "Map" button that opened a drawer to draw the instance the stream belonged
// to. Everything the second table showed was a CONSEQUENCE of something in the first — a stream
// exists because a hop opened it — and the page made a reader carry a stream id from one to the
// other by eye, then open a modal to see the relationship it had just reconstructed.
//
// So: the table lists ROUTES, as it did, with the SR14 numbers it had. A row EXPANDS, and inside it
// are the streams that route is actually carrying (joined by stream id — an identity, never a guess
// on time or tool name) beside the route's own graph, which is the same <RouteGraph/> the editor
// draws. On top: a status filter defaulting to ACTIVE (the page is opened mid-call; finished routes
// arriving first bury the live row), free-text search across route/tool/stream, and a pager.
//
// Streams that belong to NO route keep a list of their own below. A plain streaming tool call
// produces exactly such a stream, and folding the two tables together must not quietly stop
// listing them.

const ROUTE_STATUS_FILTERS = ['active', 'completed', 'errored', 'all'] as const;
export type RouteStatusFilter = (typeof ROUTE_STATUS_FILTERS)[number];
const ROUTE_STATUS_LABEL: Record<RouteStatusFilter, string> = {
  active: 'Running now',
  completed: 'Completed',
  errored: 'Errored',
  all: 'All',
};

/** Rows per page, for both tables on the panel. */
export const ROUTE_PAGE_SIZE = 10;

/** Does a route row survive the status filter? */
export function matchesRouteStatus(status: string, filter: RouteStatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'active') return status === 'active';
  return status === filter;
}

/** A route's stream leg, joined to whatever `GET /streams` still knows about that stream.
 *  `stream` is null for a leg whose stream has already been torn down — which is a fact worth
 *  drawing, not a row worth dropping. */
export interface JoinedLeg extends StreamLeg {
  stream: StreamRow | null;
}

/** One row of the merged table. */
export interface RouteStreamRow {
  row: InstanceRow;
  /** The REST instance — the only thing that knows which streams this route carries. A row that
   *  arrived purely from a `route.*` bus frame has none yet, and says so instead of showing zero. */
  instance: RouteInstance | null;
  def?: Route;
  legs: JoinedLeg[];
  /** Lower-cased text the search box matches against. */
  haystack: string;
}

/** Join route instances to the live streams they carry. Pure, so the join and the search text can
 *  be pinned by a test without rendering anything. */
export function buildRouteStreamRows(
  rows: InstanceRow[],
  instances: RouteInstance[],
  streams: Map<string, StreamRow>,
  defs: Map<string, Route>,
): RouteStreamRow[] {
  const byId = new Map(instances.map((i) => [i.instance_id, i]));
  return rows.map((row) => {
    const instance = byId.get(row.instance_id) ?? null;
    const def = defs.get(`${row.route_name}@${row.version}`);
    const legs: JoinedLeg[] = instance
      ? instanceLegs(instance).map((l) => ({ ...l, stream: streams.get(l.streamId) ?? null }))
      : [];
    return {
      row,
      instance,
      def,
      legs,
      haystack: routeHaystack([
        row.route_name,
        def?.description,
        row.instance_id,
        row.status,
        ...row.hops,
        ...legs.map((l) => l.streamId),
        ...legs.map((l) => l.stream?.media_type),
      ]),
    };
  });
}

/** The streams of the SELECTED network. A stream's only link back to a network is the
 *  `{slug}__service__tool` prefix of the tool that opened it; a stream that does not name its tool
 *  is kept rather than hidden, because "we could not tell" and "it is not yours" are different
 *  answers and only one of them should remove a row. */
export function streamsOfNetwork(streams: StreamRow[], networkSlug: string): StreamRow[] {
  if (!networkSlug) return streams;
  return streams.filter((s) => !s.canonical_tool || s.canonical_tool.split('__')[0] === networkSlug);
}

export function RouteStreamsPanel({
  networkId,
  networkSlug,
}: {
  networkId: string;
  networkSlug: string;
}) {
  // SR14: the numbers move every second, so the seed is RE-POLLED rather than fetched once —
  // acceptance 1 is literally "two snapshots five seconds apart differ, with no page reload".
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  // ONE request where there used to be two (`active` + `completed`): `all` is live + history
  // together, and the status filter below decides which half the table shows.
  const seed = useResource(() => api.routeInstances(networkId, 'all'), [networkId, tick]);
  const streamsRes = useResource(() => api.streams(), [tick]);
  // CL13 — an instance says WHAT ran, the route definition says how it was WIRED (`from`,
  // `output_hop`). Every version is fetched, not only the latest, because a running instance pins
  // the version it opened on and drawing it with a newer shape would show a graph nobody ran.
  const defs = useResource(() => api.routes(networkId, true), [networkId]);
  const liveInstances = useStore((s) => s.routeInstances);
  const liveStreams = useStore((s) => s.streams);

  const [status, setStatus] = usePersistentState<RouteStatusFilter>(
    'streams.routes.status',
    'active',
  );
  const [query, setQuery] = useState('');
  const [routePage, setRoutePage] = useState(0);
  const [loosePage, setLoosePage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const [watching, setWatching] = useState<StreamRow | null>(null);
  const [watchUrl, setWatchUrl] = useState<string | null>(null);
  const [watchErr, setWatchErr] = useState<string | null>(null);

  // A narrowed filter must not leave the reader on a page that no longer exists. `pageSlice`
  // clamps, but resetting is the honest behaviour: the first match is what they searched for.
  useEffect(() => {
    setRoutePage(0);
    setLoosePage(0);
  }, [query, status, networkId]);

  async function startWatch(s: StreamRow): Promise<void> {
    setWatching(s);
    setWatchUrl(null);
    setWatchErr(null);
    try {
      const t = await api.streamWatchToken(s.id);
      // Build the WS url against the dashboard's own gateway origin (config.wsUrl), carrying the
      // minted token — the server's ws_url may point at an internal public_base_url.
      setWatchUrl(wsUrl(`/streams/${encodeURIComponent(s.id)}?token=${encodeURIComponent(t.token)}`));
    } catch (e) {
      // A mock backend / a gateway predating ST41 has no mint endpoint (404) — fall back to the
      // placeholder token so the mock tester still renders. A real denial (403) surfaces as an error.
      if (e instanceof ApiError && e.status === 404) {
        setWatchUrl(wsUrl(`/streams/${encodeURIComponent(s.id)}?token=watch`));
      } else {
        setWatchErr(e instanceof ApiError ? e.message : String(e));
      }
    }
  }

  function closeWatch(): void {
    setWatching(null);
    setWatchUrl(null);
    setWatchErr(null);
  }

  const instances = useMemo(() => seed.data?.items ?? [], [seed.data]);

  // The instance rows: REST truth, overlaid with whatever `route.*` frames have landed since.
  const merged = useMemo(() => {
    const map = new Map<string, InstanceRow>();
    for (const i of instances) map.set(i.instance_id, instanceRow(i));
    for (const i of Object.values(liveInstances)) {
      if (i.network_id !== networkId) continue;
      // A route.* bus frame carries identity, not measurements: keep whatever the REST poll last
      // read rather than blanking the numbers every time an event lands.
      const prior = map.get(i.instance_id);
      map.set(i.instance_id, {
        ...(prior ?? { network_id: i.network_id, metrics: null }),
        instance_id: i.instance_id,
        route_name: i.route_name,
        version: i.version,
        status: i.status,
        hops: i.hops.length ? i.hops : (prior?.hops ?? []),
        network_id: i.network_id,
      });
    }
    return [...map.values()].sort((a, b) => a.route_name.localeCompare(b.route_name));
  }, [instances, liveInstances, networkId]);

  // Live overlay carries the §5A channel descriptor + current consumer/state; the
  // REST seed carries media_type/bytes/metrics. Merge by stream id (live wins on overlap).
  const streamsById = useMemo(() => {
    const map = new Map<string, StreamRow>();
    for (const s of streamsRes.data?.items ?? []) {
      map.set(s.id, {
        id: s.id,
        canonical_tool: s.canonical_tool,
        media_type: s.media_type,
        status: s.status,
        consumer_count: s.consumer_count,
        bytes: s.bytes,
        channels: s.channels ?? [],
        metrics: s.metrics ?? null,
        backpressure: s.backpressure,
      });
    }
    for (const l of Object.values(liveStreams)) {
      const prev = map.get(l.id);
      map.set(l.id, {
        id: l.id,
        canonical_tool: prev?.canonical_tool,
        media_type: prev?.media_type,
        status: l.state,
        consumer_count: l.consumer_count,
        bytes: prev?.bytes ?? 0,
        channels: l.channels.length ? l.channels : (prev?.channels ?? []),
        metrics: prev?.metrics ?? null,
        backpressure: prev?.backpressure,
      });
    }
    return map;
  }, [liveStreams, streamsRes.data]);

  const defsByVersion = useMemo(() => {
    const m = new Map<string, Route>();
    for (const r of defs.data?.items ?? []) m.set(`${r.name}@${r.version}`, r);
    return m;
  }, [defs.data]);

  const rows = useMemo(
    () => buildRouteStreamRows(merged, instances, streamsById, defsByVersion),
    [merged, instances, streamsById, defsByVersion],
  );

  const visible = useMemo(
    () => rows.filter((r) => matchesRouteStatus(r.row.status, status) && matchesQuery(r.haystack, query)),
    [rows, status, query],
  );
  const routePaged = pageSlice(visible, routePage, ROUTE_PAGE_SIZE);

  // Streams no route in this network claims: a plain streaming tool call, or a leg of a route in
  // another network. Both are real, and neither has a home in the table above.
  const loose = useMemo(() => {
    const scoped = streamsOfNetwork([...streamsById.values()], networkSlug);
    const { unattached } = splitByRoute(scoped, instances);
    return unattached.filter((s) =>
      matchesQuery(routeHaystack([s.id, s.canonical_tool, s.media_type, s.status]), query),
    );
  }, [streamsById, networkSlug, instances, query]);
  const loosePaged = pageSlice(loose, loosePage, ROUTE_PAGE_SIZE);

  const toggle = (id: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const filtersActive = query.length > 0 || status !== 'active';

  if (seed.loading && !seed.data) return <Loading what="Loading routes…" />;
  if (seed.error && !seed.data) return <ErrorState message={seed.error} onRetry={seed.reload} />;

  return (
    <>
      <div className="catalog-toolbar">
        <input
          className="catalog-search"
          type="search"
          placeholder="Search route, description, tool, stream id…"
          aria-label="search routes and streams"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          value={status}
          aria-label="route status"
          onChange={(e) => setStatus(e.target.value as RouteStatusFilter)}
        >
          {ROUTE_STATUS_FILTERS.map((f) => (
            <option key={f} value={f}>
              {ROUTE_STATUS_LABEL[f]}
            </option>
          ))}
        </select>
        {filtersActive && (
          <button
            className="btn btn-sm"
            onClick={() => {
              setQuery('');
              setStatus('active');
            }}
          >
            Clear
          </button>
        )}
        <span className="muted small">
          {visible.length} of {rows.length} routes
        </span>
      </div>

      {streamsRes.error && (
        <p className="warn-text">
          The live stream list is unreachable ({streamsRes.error}) — a route below shows its hops and
          its numbers, but not the streams it is carrying. That is why they are missing, not because
          it is carrying none.
        </p>
      )}

      {visible.length === 0 ? (
        <div className="empty">
          {filtersActive
            ? 'No route matches this filter.'
            : 'Nothing running in this network right now.'}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table route-stream-table">
            <thead>
              <tr>
                <th style={{ width: '26px' }}>
                  <span className="sr-only">Expand</span>
                </th>
                <th>Route</th>
                <th>Version</th>
                <th>Hops</th>
                <th style={{ textAlign: 'right' }}>Streams</th>
                <th>Throughput</th>
                <th>Latency p50/p95</th>
                <th>Drops</th>
                <th>Path</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {routePaged.rows.map((r) => {
                const open = expanded.has(r.row.instance_id);
                const label = routeLabel({ name: r.row.route_name, description: r.def?.description });
                const id = idLabel(r.row.instance_id);
                return (
                  <Fragment key={r.row.instance_id}>
                    <tr
                      className="clickable"
                      data-testid="route-row"
                      aria-expanded={open}
                      onClick={() => toggle(r.row.instance_id)}
                    >
                      <td>
                        <span className="collapse-chevron" aria-hidden="true">
                          {open ? '▾' : '▸'}
                        </span>
                      </td>
                      <td>
                        <b>{label.name}</b>
                        {label.sub && <div className="small muted">{label.sub}</div>}
                        <div className="small mono muted" title={id.title}>
                          {id.label}
                        </div>
                      </td>
                      <td>
                        <Badge tone="muted">v{r.row.version}</Badge>
                      </td>
                      <td className="small">
                        {r.row.hops.map((tool, idx) => {
                          const t = toolLabel(tool);
                          return (
                            <div key={`${tool}-${idx}`} title={t.title}>
                              {idx > 0 ? '→ ' : ''}
                              {t.label}
                              {/* CL13: a tap is a SIDE branch, so "→" between it and the hop above
                                  it is a picture of a chain that does not exist. Naming it is also
                                  what keeps its silence from being read as a fault: a VAD says
                                  nothing most of the time. */}
                              {r.row.hopIsTap?.[idx] ? <Badge tone="info">tap</Badge> : null}
                              {/* SR21: the point of the watchdog is that ONE link is named. */}
                              {r.row.hopStatus?.[idx] === 'degraded' ? (
                                <Badge tone="warn">{formatHopStall(r.row.hopDegradedAfter?.[idx])}</Badge>
                              ) : null}
                            </div>
                          );
                        })}
                      </td>
                      <td style={{ textAlign: 'right' }} className="mono">
                        {r.instance ? r.legs.length : '—'}
                      </td>
                      <td className="small mono">{formatThroughput(r.row.metrics)}</td>
                      <td className="small mono">{formatLatency(r.row.metrics)}</td>
                      <td className="small mono">
                        {r.row.metrics ? (
                          <Badge tone={r.row.metrics.drops > 0 ? 'warn' : 'muted'}>
                            {String(r.row.metrics.drops)}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="small mono">{r.row.metrics?.delivery_path ?? '—'}</td>
                      <td>
                        <StatusBadge status={r.row.status} />
                      </td>
                    </tr>
                    {open && (
                      <tr className="route-detail-row" data-testid="route-detail">
                        <td colSpan={10}>
                          <RouteDetail row={r} onWatch={(s) => void startWatch(s)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pager
        page={routePaged.page}
        pageCount={routePaged.pageCount}
        total={visible.length}
        onPage={setRoutePage}
        label="Routes"
      />

      <div className="panel-actions">
        <span className="muted small">Streams outside any route ({loose.length})</span>
      </div>
      {loose.length === 0 ? (
        <p className="muted small">
          {query
            ? 'No unrouted stream matches this search.'
            : 'Every live stream in this network belongs to a route above.'}
        </p>
      ) : (
        <>
          <StreamTable
            legs={loosePaged.rows.map((s) => ({
              streamId: s.id,
              hop: null,
              role: 'plain tool call',
              isOutput: false,
              stream: s,
            }))}
            onWatch={(s) => void startWatch(s)}
            testId="loose-stream"
          />
          <Pager
            page={loosePaged.page}
            pageCount={loosePaged.pageCount}
            total={loose.length}
            onPage={setLoosePage}
            label="Unrouted streams"
          />
        </>
      )}

      {watching && (
        <Drawer
          title={
            <span>
              Stream <code className="mono">{watching.id}</code>
            </span>
          }
          subtitle={watching.media_type ?? undefined}
          onClose={closeWatch}
          width={520}
        >
          {/* ST41: the consumer token is minted per-watch via POST /streams/{id}/watch-token
              (single-stream-scoped, read-only, short-TTL) and carried in the WS url. */}
          {watchErr ? (
            <ErrorState message={watchErr} onRetry={() => void startWatch(watching)} />
          ) : watchUrl ? (
            <StreamViewer wsUrl={watchUrl} mediaType={watching.media_type} onClose={closeWatch} />
          ) : (
            <Loading />
          )}
        </Drawer>
      )}
    </>
  );
}

/** What an expanded route row contains: the route drawn, and the streams it is carrying.
 *
 *  The graph comes first on purpose. "Which link" is the question the expansion is opened with,
 *  and the stream table underneath is the evidence for the answer. */
function RouteDetail({ row, onWatch }: { row: RouteStreamRow; onWatch: (s: StreamRow) => void }) {
  return (
    <div className="route-detail">
      <InstanceGraph row={row.row} def={row.def} />
      <div className="panel-actions">
        <span className="muted small">Streams this route is carrying ({row.legs.length})</span>
      </div>
      {!row.instance ? (
        <p className="muted small">
          This row arrived as a <code className="mono">route.*</code> event and has not been seen by
          a route-instance poll yet, so the gateway has not said which streams it opened.
        </p>
      ) : row.legs.length === 0 ? (
        <p className="muted small">
          The gateway reports no streams for this instance — it was torn down, or it never got past
          instantiation.
        </p>
      ) : (
        <StreamTable legs={row.legs} onWatch={onWatch} />
      )}
    </div>
  );
}

/** The stream table — used both inside an expanded route and for the unrouted streams below it,
 *  because they are the same subject and deserve the same columns. */
function StreamTable({
  legs,
  onWatch,
  testId = 'stream-leg',
}: {
  legs: JoinedLeg[];
  onWatch: (s: StreamRow) => void;
  /** The two uses of this table are different subjects to a test — a route's own legs, and the
   *  streams no route claims — so they are labelled apart. */
  testId?: string;
}) {
  return (
    <div className="table-wrap">
      <table className="data-table stream-leg-table">
        <thead>
          <tr>
            <th>Stream</th>
            <th>Role</th>
            <th>Channels</th>
            <th>Throughput</th>
            <th>Status</th>
            <th style={{ textAlign: 'right' }}>Consumers</th>
            <th style={{ textAlign: 'right' }}>Bytes</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {legs.map((leg) => {
            const s = leg.stream;
            const name = s ? streamLabel(s) : null;
            const id = idLabel(leg.streamId);
            const tool = leg.tool ? toolLabel(leg.tool) : null;
            return (
              <tr key={leg.streamId} data-testid={testId}>
                <td>
                  {/* The friendly name is the channel's or the media type's; the exact stream id is
                      never dropped — it is the handle every log line and every gateway call uses,
                      so it stays on the row, shortened, with the full value one hover away. */}
                  {name && <b>{name.label}</b>}
                  <div className="small mono muted" title={id.title}>
                    {id.label}
                  </div>
                </td>
                <td className="small">
                  {leg.role}
                  {leg.isOutput && <Badge tone="ok">out</Badge>}
                  {tool && (
                    <div className="muted small" title={tool.title}>
                      {tool.label}
                    </div>
                  )}
                </td>
                <td>
                  {s && s.channels.length ? (
                    <span className="muted-row">
                      {s.channels.map((c) => (
                        <span key={c.channel_id} className="chip">
                          {c.direction === 'UP' ? '▲' : '▼'} {c.name}
                        </span>
                      ))}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="small mono" title={s?.backpressure ? `backpressure: ${s.backpressure}` : undefined}>
                  {formatThroughput(s?.metrics)}
                </td>
                <td>
                  {s ? (
                    <StatusBadge status={s.status} />
                  ) : (
                    // A leg the route still names but the hub no longer holds. Saying "closed" is
                    // the whole point: the alternative is an absent row, and an absence with no
                    // name is what somebody debugs from the browser for an hour.
                    <Badge tone="muted">closed</Badge>
                  )}
                </td>
                <td style={{ textAlign: 'right' }} className="mono">
                  {s ? s.consumer_count : '—'}
                </td>
                <td style={{ textAlign: 'right' }} className="mono">
                  {s ? bytesHuman(s.bytes) : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn btn-sm"
                    disabled={!s || s.status === 'CLOSED'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (s) onWatch(s);
                    }}
                  >
                    Watch
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One live instance drawn as the graph it is, with each hop's own state ON the hop.
 *
 * The table below still answers "how fast"; this answers "which link", which is the question the
 * SR21 watchdog and the CL13 branch both exist to make answerable. A silent call and a call in
 * which nobody is speaking are the same row in a table and different pictures here: the degraded
 * badge names ONE hop, and a sidecar that was never instantiated is drawn switched off rather than
 * missing — an absence with no name is what somebody debugs from the browser for an hour. */
export function InstanceGraph({ row, def }: { row: InstanceRow; def?: Route }) {
  const shape = def
    ? { hops: def.hops, outputHop: def.output_hop ?? null }
    : shapeFromInstance(
        (row.hopIndex ?? row.hops.map((_, i) => i)).map((index, pos) => ({
          index,
          canonical_tool: row.hops[pos] ?? '—',
          is_tap: row.hopIsTap?.[pos],
        })),
        row.skipped ?? [],
      );
  const graph = buildRouteGraph(shape.hops, shape.outputHop);
  const live: Record<number, HopLiveState> = {};
  (row.hopIndex ?? row.hops.map((_, i) => i)).forEach((index, pos) => {
    const status = row.hopStatus?.[pos];
    live[index] = {
      status,
      detail: formatThroughput(row.hopMetrics?.[pos]),
      note:
        status === 'degraded'
          ? formatHopStall(row.hopDegradedAfter?.[pos] ?? row.hopIdle?.[pos])
          : undefined,
    };
  });
  for (const s of row.skipped ?? []) {
    // Not an error on the route: nothing in the chain reads a terminal branch, so the call carries
    // exactly the frames it carried before the branch was written (CL13).
    live[s.hop] = { status: 'skipped', note: 'no provider — sidecar off', detail: s.reason || undefined };
  }

  return (
    <div className="route-instance-graph">
      <div className="small mono muted">
        {row.route_name} v{row.version} · {row.instance_id}
      </div>
      <RouteGraph graph={graph} live={live} title={`${row.route_name} instance graph`} />
      {(row.skipped ?? []).length > 0 && (
        <p className="warn-text" role="status">
          {(row.skipped ?? [])
            .map((s) => `branch hop ${s.hop} (${s.tool}) was left out: ${s.reason || 'no live provider'}`)
            .join(' · ')}{' '}
          — the route runs without it.
        </p>
      )}
    </div>
  );
}

export interface InstanceRowMetrics {
  frames_per_second: number;
  bytes_per_second: number;
  latency_p50_ms: number;
  latency_p95_ms: number;
  drops: number;
  delivery_path: string;
}

export interface InstanceRow {
  instance_id: string;
  route_name: string;
  version: number;
  status: string;
  hops: string[];
  network_id: string;
  metrics?: InstanceRowMetrics | null;
  hopMetrics?: (InstanceRowMetrics | null | undefined)[];
  // SR21 — per-hop `live` | `degraded`, so the table can say WHICH link went quiet. A call that
  // goes silent used to look exactly like a call in which nobody is speaking.
  hopStatus?: (string | undefined)[];
  hopDegradedAfter?: (number | null | undefined)[];
  // CL13 — a hop is identified by its INDEX, not by its position in this list: a branch that had no
  // provider is absent, and reading position N would then attribute one hop's numbers to another.
  hopIndex?: number[];
  hopIsTap?: (boolean | undefined)[];
  hopIdle?: (number | null | undefined)[];
  skipped?: RouteSkippedHop[];
}

/** SR14 — the throughput cell. Absent metrics read as "not measured", never as a hop count. */
export function formatThroughput(m?: InstanceRowMetrics | null): string {
  if (!m) return '—';
  const kbps = (m.bytes_per_second * 8) / 1000;
  return `${m.frames_per_second.toFixed(1)} fps · ${kbps.toFixed(0)} kbit/s`;
}

/** SR21 — the badge on a hop the idle watchdog called out, carrying HOW LONG it has been mute.
 *
 * "degraded" alone would make an operator open a log to learn whether it was two seconds or two
 * minutes, and those are different faults. */
export function formatHopStall(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return 'no output';
  return `no output ${seconds < 60 ? `${Math.round(seconds)}s` : `${Math.round(seconds / 60)}m`}`;
}

/** SR14 — the latency cell: p50/p95, because a mean hides the stall that people hear. */
export function formatLatency(m?: InstanceRowMetrics | null): string {
  if (!m) return '—';
  return `${m.latency_p50_ms.toFixed(0)} / ${m.latency_p95_ms.toFixed(0)} ms`;
}

// --- the stream row, and where a stream sits inside a route --------------------

export interface StreamRow {
  id: string;
  /** WB6 — `{network}__{service}__{tool}`: a stream's only link back to a network, and the label
   *  the friendly-name helpers read the service and tool out of. */
  canonical_tool?: string;
  media_type?: string;
  status: string;
  consumer_count: number;
  bytes: number;
  channels: { channel_id: number; name: string; direction: string; media_type?: string }[];
  /** WB6 — the SR14 block `GET /streams` now carries (`null` on a gateway that predates it). */
  metrics?: InstanceRowMetrics | null;
  /** ADR-H — `lossless` | `drop_oldest`: what this stream's `drops` count MEANS. */
  backpressure?: string;
}

// `placeStream` and the leg walk live in `lib/routeStreams` so the join can be tested without a
// renderer; re-exported here because it is the Streams view's own vocabulary.
export { placeStream } from '../lib/routeStreams';
export type { StreamPlacement } from '../lib/routeStreams';

/** A live RouteInstance as the row shape <InstanceGraph/> already knows how to draw. */
export function instanceRow(i: RouteInstance): InstanceRow {
  return {
    instance_id: i.instance_id,
    route_name: i.route_name,
    version: i.version,
    status: i.status,
    hops: i.hops.map((h) => h.canonical_tool),
    network_id: i.network_id,
    metrics: i.metrics ?? null,
    hopMetrics: i.hops.map((h) => h.metrics ?? null),
    hopStatus: i.hops.map((h) => h.status),
    hopDegradedAfter: i.hops.map((h) => h.degraded_after_s ?? null),
    hopIndex: i.hops.map((h) => h.index),
    hopIsTap: i.hops.map((h) => h.is_tap),
    hopIdle: i.hops.map((h) => h.metrics?.idle_seconds ?? null),
    skipped: i.skipped_hops ?? [],
  };
}

// --- latency budget (SR19) ------------------------------------------------
//
// The panel exists because "the call feels slow" is not a fact anybody can act on, and the rows of
// the epic's budget table are. It reads two clocks and says on every row which one:
//
//   * the ASSISTANT's voice metric family (`/assistant-api/v1/metrics/summary` -> `voice`), which
//     owns the per-stage marks: end of speech to final transcript, LLM first token, reply text,
//     TTS first audio, and the end-to-end mouth-to-ear;
//   * the GATEWAY's per-hop stream metrics (SR14, on the route instances the panel above already
//     lists), which own the transport row and nothing else -- that latency is the gateway's
//     queue-and-write share, not the model's and not the caller's.
//
// Two rows have no clock at all on this stand (mic capture, the player's jitter buffer). They stay
// in the table saying "not measured", because a budget that drops its un-timed rows reads as
// complete and moves the blame onto the rows that did report.

const LATENCY_WINDOWS = ['900', '3600', '86400'] as const;
type LatencyWindow = (typeof LATENCY_WINDOWS)[number];
const WINDOW_LABEL: Record<LatencyWindow, string> = {
  '900': '15 min',
  '3600': '1 hour',
  '86400': '24 hours',
};

function LatencyBudget({ networkId }: { networkId: string }) {
  const [windowSeconds, setWindowSeconds] = usePersistentState<LatencyWindow>(
    'streams.latency.window',
    '3600',
  );
  // The same five-second beat as the instances panel: a budget read while a call is live has to
  // move, or an operator watching one cannot tell a recovering stage from a frozen page.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const voiceRes = useResource(
    () => api.assistantMetricsSummary(Number(windowSeconds)),
    [windowSeconds, tick],
  );
  // `all`, not the live default: the interesting calls are the ones that have ENDED, and SR14 kept
  // their hop numbers precisely so a question asked after the fact still has an answer.
  const instances = useResource(() => api.routeInstances(networkId, 'all'), [networkId, tick]);

  const voice = voiceRes.data?.voice ?? null;
  const hops = useMemo(
    () =>
      worstHopLatency(
        (instances.data?.items ?? []).flatMap((i) => i.hops.map((h) => h.metrics ?? null)),
      ),
    [instances.data],
  );

  const rows = useMemo(() => buildBudget(voice, hops), [voice, hops]);
  const replyText = useMemo(() => replyTextRow(voice), [voice]);
  const realtime = useMemo(() => byMode(voice, REALTIME_ASR), [voice]);
  const ptt = useMemo(() => byMode(voice, PTT_ASR), [voice]);
  const overall = voice?.stages?.mouth_to_ear ?? { count: 0, p50_ms: 0, p95_ms: 0, max_ms: 0 };
  // The verdict is taken on the REALTIME samples wherever there are any: 800 ms is the streaming
  // route's budget, and judging it over a window that also holds push-to-talk turns would blame one
  // tract for the other's numbers. With no realtime sample, fall back to the whole family -- and
  // the sample count on the card is what says which population that was.
  const verdict = useMemo(
    () => budgetVerdict(realtime.count ? realtime : overall, [...rows, replyText]),
    [realtime, overall, rows, replyText],
  );

  if (voiceRes.loading && !voiceRes.data) return <Loading what="Loading latency…" />;

  // UX23 — the same de-nesting the editor and the instances got: this component is
  // already inside the "Latency" section, so its two inner Cards were a second heading
  // and a second border around content that already had both.
  return (
    <>
      <div className="panel-actions">
        <span className="muted small">Mouth to ear</span>
      </div>
      <div className="panel-body">
        <div className="latency-head">
          <Segmented<LatencyWindow>
            label="Window"
            options={LATENCY_WINDOWS.map((w) => ({ value: w, label: WINDOW_LABEL[w] }))}
            value={windowSeconds}
            onChange={setWindowSeconds}
          />
        </div>
        {voiceRes.error && (
          <p className="warn-text">
            The assistant’s metrics are unreachable ({voiceRes.error}) — the per-stage rows below
            are blank for that reason, not because those stages are fast.
          </p>
        )}

        <div className="stat-grid latency-stats">
          <StatCard
            label={`Realtime route · target ${MOUTH_TO_EAR_TARGET_MS} ms`}
            value={formatStat(
              realtime.count ? realtime.p50_ms : null,
              realtime.count ? realtime.p95_ms : null,
            )}
            sub={realtime.count ? `p50 / p95 · ${realtime.count} turns` : 'no turns in this window'}
          />
          <StatCard
            label={`Push-to-talk · target ${PTT_TARGET_MS} ms`}
            value={formatStat(ptt.count ? ptt.p50_ms : null, ptt.count ? ptt.p95_ms : null)}
            sub={ptt.count ? `p50 / p95 · ${ptt.count} turns` : 'no turns in this window'}
          />
          <StatCard
            label="Verdict"
            value={
              verdict.met === null ? (
                <span className="muted">no data</span>
              ) : verdict.met ? (
                <span className="ok-text">target met</span>
              ) : (
                <span className="error-text">target missed</span>
              )
            }
            sub={
              verdict.met === false && verdict.overMs !== null
                ? `${Math.round(verdict.overMs)} ms over ${verdict.targetMs} ms · ${verdict.samples} turns`
                : verdict.met === null
                  ? 'nothing measured in this window'
                  : `${verdict.samples} turns within ${verdict.targetMs} ms`
            }
          />
        </div>

        {verdict.met === false && verdict.offenders.length > 0 && (
          <p className="error-text">
            Over budget by {Math.round(verdict.overMs ?? 0)} ms. Where it went:{' '}
            {verdict.offenders
              .slice(0, 3)
              .map((o) => `${o.label} +${Math.round(o.overMs)} ms`)
              .join('; ')}
            .
          </p>
        )}
        {verdict.met === false && verdict.offenders.length === 0 && (
          <p className="warn-text">
            Over budget, but no single row is over its own — the milliseconds are either in the
            stages this stand does not time (mic capture, the player’s jitter buffer) or spread
            thinly across all of them.
          </p>
        )}
      </div>

      <div className="panel-actions">
        <span className="muted small">Per-stage budget</span>
      </div>
      <div className="panel-body">
        <table className="table latency-table">
          <thead>
            <tr>
              <th>Stage</th>
              <th>p50 / p95</th>
              <th>Budget</th>
              <th>Against budget</th>
              <th>Samples</th>
              <th>Clock</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <BudgetRowCells key={r.id} row={r} />
            ))}
            <tr>
              <td colSpan={6} className="small muted">
                Measured end to end on the assistant hop rather than summed from the rows above —
                the half of the budget this stand can prove with one subtraction of two marks:
              </td>
            </tr>
            <BudgetRowCells row={replyText} />
          </tbody>
        </table>
        <p className="small muted">
          The gateway row is the worst hop of {instances.data?.items.length ?? 0} route instances in
          this network — its own queue-and-write share, deliberately not the model’s time and not
          the caller’s.
        </p>
      </div>
    </>
  );
}

/** One row of the budget table: the number, the budget it is read against, and its bar. */
function BudgetRowCells({ row }: { row: BudgetRow }) {
  const sev = budgetSeverity(row);
  return (
    <tr className={row.samples ? undefined : 'latency-row-empty'} data-stage={row.id}>
      <td>
        {row.label}
        {row.note && <div className="small muted">{row.note}</div>}
      </td>
      <td className="mono">{formatStat(row.p50Ms, row.p95Ms)}</td>
      <td className="mono muted">≤ {row.targetMs} ms</td>
      <td>
        {sev === 'none' ? (
          <span className="muted small">not measured</span>
        ) : (
          <div className="progress" title={`${Math.round(row.p50Ms ?? 0)} of ${row.targetMs} ms`}>
            <div
              className={`progress-fill sev-${sev}`}
              style={{ width: `${Math.round(budgetFill(row) * 100)}%` }}
            />
          </div>
        )}
      </td>
      <td className="mono">{row.samples || '—'}</td>
      <td className="small muted">{row.source}</td>
    </tr>
  );
}
